import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useLang } from "../i18n/LanguageContext";
import { Reveal } from "./Reveal";

export const Overline = ({ children, className = "" }) => (
  <span className={`inline-flex items-center gap-3 font-sans text-[0.65rem] uppercase tracking-[0.3em] text-kenosis-gold ${className}`}>
    {children}
  </span>
);

export const PrimaryButton = ({ children, onClick, testid, className = "" }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={`group relative inline-flex items-center overflow-hidden border border-kenosis-gold/60 px-8 py-4 ${className}`}
  >
    <span className="relative z-10 font-sans text-xs uppercase tracking-[0.2em] text-kenosis-gold transition-colors duration-500 group-hover:text-kenosis-black">
      {children}
    </span>
    <span className="absolute inset-0 z-0 origin-left scale-x-0 bg-kenosis-gold transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-x-100" />
  </button>
);

export const GhostLink = ({ children, onClick, testid }) => (
  <button data-testid={testid} onClick={onClick} className="group inline-flex items-center gap-2 font-sans text-xs uppercase tracking-[0.2em] text-kenosis-ivory/80 transition-colors hover:text-kenosis-gold">
    {children}
    <span className="inline-block transition-transform duration-300 group-hover:translate-x-1">&rarr;</span>
  </button>
);

// Page hero used by inner pages
export const PageHero = ({ overline, title, intro, image }) => (
  <section className="relative pt-40 md:pt-52 pb-20 md:pb-28 overflow-hidden">
    {image && (
      <div className="absolute inset-0 z-0">
        <img src={image} alt="" className="h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-kenosis-black/85 via-kenosis-black/90 to-kenosis-black" />
        <div className="absolute inset-0 bg-kenosis-black/35" />
      </div>
    )}
    <div className="relative z-10 mx-auto max-w-[1400px] px-6 md:px-10">
      <Reveal><Overline>{overline}</Overline></Reveal>
      <Reveal delay={0.1}>
        <h1 className={`mt-8 max-w-4xl font-serif text-5xl sm:text-6xl md:text-7xl font-light leading-[1.02] tracking-tight text-kenosis-ivory ${image ? "text-outline-strong" : ""}`}>
          {title}
        </h1>
      </Reveal>
      {intro && (
        <Reveal delay={0.2}>
          <p className={`mt-8 max-w-2xl font-sans text-base md:text-lg font-light leading-relaxed text-kenosis-ivory/80 ${image ? "text-outline" : ""}`}>
            {intro}
          </p>
        </Reveal>
      )}
    </div>
  </section>
);

export const CtaBand = () => {
  const { t } = useLang();
  const navigate = useNavigate();
  return (
    <section className="relative border-t border-white/10 py-28 md:py-40">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 text-center">
        <Reveal><Overline className="justify-center">{t("home.finalOverline")}</Overline></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mx-auto mt-8 max-w-3xl font-serif text-4xl md:text-6xl font-light leading-[1.05] tracking-tight text-kenosis-ivory">
            {t("home.finalTitle")}
          </h2>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mx-auto mt-6 max-w-xl font-sans text-sm md:text-base font-light text-kenosis-muted">
            {t("home.finalSub")}
          </p>
        </Reveal>
        <Reveal delay={0.3}>
          <div className="mt-12 flex justify-center">
            <PrimaryButton testid="cta-band-button" onClick={() => navigate("/apply")}>{t("common.cta")}</PrimaryButton>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export const easeLux = [0.22, 1, 0.36, 1];
export { motion };
